import {
  Bsff,
  BsdType,
  BsffType,
  Query,
  QueryBsffArgs,
  Mutation,
  MutationUpdateBsffArgs,
  MutationCreateDraftBsffArgs,
  MutationPublishBsffArgs,
  BsffInput,
  MutationCreateBsffTransporterArgs,
  MutationCreateBsffArgs,
  MutationUpdateBsffTransporterArgs,
  BsffTransporterInput,
  TransportMode,
  BsffDestinationInput,
  MutationCreateFicheInterventionBsffArgs,
  MutationUpdateFicheInterventionBsffArgs
} from "@td/codegen-ui";

import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_DRAFT_BSFF,
  GET_BSFF_FORM,
  CREATE_BSFF,
  PUBLISH_BSFF,
  UPDATE_BSFF_FORM,
  CREATE_BSFF_FICHE_INTERVENTION,
  UPDATE_BSFF_FICHE_INTERVENTION
} from "../../../common/queries/bsff/queries";

import {
  CREATE_BSFF_TRANSPORTER,
  UPDATE_BSFF_TRANSPORTER
} from "../../../Forms/Components/query";

import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo, createContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { rawBsffSchema, ZodBsff } from "./schema";
import FormStepsContent from "../FormStepsContent";
import { Loader } from "../../../common/Components";
import initialState from "./utils/initial-state";
import { getComputedState } from "../getComputedState";
import WasteBsff from "./steps/Waste";
import EmitterBsff from "./steps/Emitter";
import DestinationBsff from "./steps/Destination";
import TransporterBsff from "./steps/Transporter";
import DetenteurBsff from "./steps/Detenteur";
import { isForeignVat } from "@td/constants";
import {
  getErrorTabIds,
  getPublishErrorMessages,
  getPublishErrorTabIds,
  handleGraphQlError,
  TabId
} from "../utils";
import { DsfrNotificationError } from "../../../common/Components/Error/Error";

interface Props {
  bsdId?: string;
  publishErrorsFromRedirect?: {
    code: string;
    path: string[];
    message: string;
  }[];
}

export const BsffContext = createContext<Bsff | undefined>(undefined);

const BsffFormSteps = ({
  bsdId,
  publishErrorsFromRedirect
}: Readonly<Props>) => {
  const [publishErrors, setPublishErrors] = useState<
    | {
        code: string;
        path: string[];
        message: string;
      }[]
    | undefined
  >();

  // ================= QUERY =================
  const bsffQuery = useQuery<Pick<Query, "bsff">, QueryBsffArgs>(
    GET_BSFF_FORM,
    {
      variables: { id: bsdId! },
      skip: !bsdId,
      fetchPolicy: "network-only"
    }
  );

  const sealedFields = useMemo(
    () =>
      (bsffQuery?.data?.bsff?.metadata?.fields?.sealed ?? [])
        ?.map(f => f.join("."))
        .filter(Boolean),
    [bsffQuery.data]
  );
  // ================= MUTATIONS =================
  const [createDraftBsff, { loading: creating }] = useMutation<
    Pick<Mutation, "createDraftBsff">,
    MutationCreateDraftBsffArgs
  >(CREATE_DRAFT_BSFF);

  const [updateBsff, { loading: updating }] = useMutation<
    Pick<Mutation, "updateBsff">,
    MutationUpdateBsffArgs
  >(UPDATE_BSFF_FORM);

  const [publishBsff, { loading: publishing }] = useMutation<
    Pick<Mutation, "publishBsff">,
    MutationPublishBsffArgs
  >(PUBLISH_BSFF);

  const [createBsffTransporter, { loading: creatingBsffTransporter }] =
    useMutation<
      Pick<Mutation, "createBsffTransporter">,
      MutationCreateBsffTransporterArgs
    >(CREATE_BSFF_TRANSPORTER);

  const [updateBsffTransporter, { loading: updatingBsffTransporter }] =
    useMutation<
      Pick<Mutation, "updateBsffTransporter">,
      MutationUpdateBsffTransporterArgs
    >(UPDATE_BSFF_TRANSPORTER);

  const [createBsff, { loading: creatingBsff, error: createBsffError }] =
    useMutation<
      Pick<Mutation, "createBsff">,
      MutationCreateBsffArgs // ← vérifier que ce type existe dans @td/codegen-ui
    >(CREATE_BSFF);

  const [createFicheIntervention, { error: ficheError }] = useMutation<
    Pick<Mutation, "createFicheInterventionBsff">,
    MutationCreateFicheInterventionBsffArgs
  >(CREATE_BSFF_FICHE_INTERVENTION);

  const [updateFicheInterventionBsff] = useMutation<
    Pick<Mutation, "updateFicheInterventionBsff">,
    MutationUpdateFicheInterventionBsffArgs
  >(UPDATE_BSFF_FICHE_INTERVENTION);

  const bsffState = useMemo(
    () =>
      getComputedState(initialState, bsffQuery.data?.bsff, [
        {
          path: "packagings",
          getComputedValue: (intialValue, actualValue) =>
            actualValue.length ? actualValue : intialValue
        },
        {
          path: "grouping",
          getComputedValue: (initialValue, actualValue) =>
            actualValue?.length ? actualValue : initialValue
        },
        {
          path: "forwarding",
          getComputedValue: (initialValue, actualValue) => {
            if (Array.isArray(actualValue)) {
              return actualValue[0] ?? initialValue;
            }
            return actualValue ?? initialValue;
          }
        },
        {
          path: "repackaging",
          getComputedValue: (initialValue, actualValue) =>
            Array.isArray(actualValue) && actualValue.length
              ? actualValue
              : initialValue
        },
        {
          path: "transporters",
          getComputedValue: (initialValue, actualValue) =>
            actualValue?.length ? actualValue : initialValue
        },
        {
          path: "ficheInterventions",
          getComputedValue: (initialValue, actualValue) =>
            actualValue?.length ? actualValue : initialValue
        }
      ]),
    [bsffQuery.data]
  );
  const methods = useForm<ZodBsff>({
    values: bsffState,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: async (data, context, options) => {
      return zodResolver(rawBsffSchema)(data, context, options);
    }
  });

  useEffect(() => {
    if (bsffState?.id && bsffQuery.data?.bsff?.id) {
      methods.reset(bsffState, {
        keepErrors: true
      });
    }
  }, [bsffState, bsffQuery.data?.bsff?.id, methods]);

  const errorsFromPublishApi = publishErrors || publishErrorsFromRedirect;
  const publishErrorTabIds = getPublishErrorTabIds(
    BsdType.Bsff,
    errorsFromPublishApi
  );

  const formStateErrorsKeys = Object.keys(methods?.formState?.errors);
  const errorTabIds = getErrorTabIds(
    BsdType.Bsff,
    publishErrorTabIds,
    formStateErrorsKeys
  );
  const publishErrorMessages = useMemo(
    () => getPublishErrorMessages(BsdType.Bsff, errorsFromPublishApi),
    [errorsFromPublishApi]
  );

  useEffect(() => {
    for (const error of publishErrorMessages) {
      methods.setError(error.name as keyof ZodBsff, {
        type: "custom",
        message: error.message
      });
    }
  }, [publishErrorMessages, methods]);

  const [bsffContext, setBsffContext] = useState<Bsff | undefined>();

  useEffect(() => {
    if (bsffQuery.data?.bsff?.id) {
      setBsffContext(bsffQuery.data.bsff);
    }
  }, [bsffQuery.data?.bsff]);

  const type = methods.watch("type");

  const tabsContent = useMemo(
    () => ({
      waste: <WasteBsff />,
      emitter:
        type === BsffType.CollectePetitesQuantites ? (
          <EmitterBsff />
        ) : undefined,
      detenteur: <DetenteurBsff />,
      transporter: <TransporterBsff />,
      destination: <DestinationBsff />
    }),
    [type]
  );

  const loading =
    creating ||
    publishing ||
    creatingBsff ||
    updating ||
    creatingBsffTransporter ||
    updatingBsffTransporter;
  const mainCtaLabel = bsffState.id ? "Enregistrer" : "Publier";
  const draftCtaLabel = bsffState.id ? "" : "Enregistrer en brouillon";

  function cleanCompany(company: any) {
    if (!company) return undefined;
    const isEmpty = Object.values(company).every(v => !v);
    return isEmpty ? undefined : company;
  }

  async function saveBsff(values: ZodBsff, draft: boolean) {
    try {
      const transporterIds = await getTransporterIds(values.transporters);
      const ficheInterventionIds = await getFicheInterventionIds(values);

      const input = buildBsffInput(
        values,
        transporterIds,
        ficheInterventionIds
      );

      if (bsffState.id) {
        return handleUpdateFlow(input, draft);
      }

      return handleCreateFlow(input, draft);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  async function getTransporterIds(transporters: any[] = []) {
    return Promise.all(
      transporters
        .filter(t => t && (t.company?.siret || t.company?.vatNumber))
        .map(async t => {
          const isSigned = !!t.transport?.signature?.date;

          if (t.id && isSigned) {
            return t.id;
          }

          return saveBsffTransporter(t);
        })
    );
  }
  async function getFicheInterventionIds(values: ZodBsff) {
    const emitterCompany = values.emitter?.company;
    if (
      [
        BsffType.Groupement,
        BsffType.Reexpedition,
        BsffType.Reconditionnement,
        BsffType.TracerFluide
      ].includes(values.type as BsffType)
    ) {
      return [];
    }
    const ficheInterventions = values.ficheInterventions ?? [];
    if (!ficheInterventions.length) return [];

    const ids = await Promise.all(
      ficheInterventions
        .filter(fi => fi.numero && fi.postalCode && fi.weight && fi.weight > 0)
        .map(async fi => {
          const detenteurCompany = fi.detenteur?.company;

          const cleanedDetenteur = {
            isPrivateIndividual: fi.detenteur?.isPrivateIndividual ?? false,
            company: detenteurCompany
              ? {
                  siret: detenteurCompany.siret ?? null,
                  name: detenteurCompany.name ?? null,
                  address: detenteurCompany.address ?? null,
                  contact: detenteurCompany.contact ?? null,
                  phone: detenteurCompany.phone ?? null,
                  mail: detenteurCompany.mail ?? null
                }
              : undefined
          };

          const ficheInput = {
            numero: fi.numero!,
            weight: Number(fi.weight),
            postalCode: fi.postalCode!,
            detenteur: cleanedDetenteur,
            operateur: { company: cleanCompany(emitterCompany) }
          };

          if (fi.id) {
            await updateFicheInterventionBsff({
              variables: { id: fi.id, input: ficheInput }
            });
            return fi.id;
          }

          const { data } = await createFicheIntervention({
            variables: { input: ficheInput }
          });

          return data?.createFicheInterventionBsff?.id ?? null;
        })
    );

    return ids.filter((id): id is string => !!id);
  }
  function buildBsffInput(
    values: ZodBsff,
    transporterIds: string[],
    ficheInterventionIds: string[]
  ): BsffInput {
    const {
      destination,
      packagings,
      type,
      grouping,
      forwarding,
      repackaging,
      waste,
      emitter,
      weight
    } = values;

    return {
      type: type as unknown as BsffType,
      emitter: emitter
        ? { ...emitter, company: cleanCompany(emitter.company) }
        : undefined,
      waste: waste?.code
        ? {
            code: waste.code,
            adr: waste.adr?.trim() || null,
            description: waste.description?.trim() || null
          }
        : undefined,
      weight: {
        value: Number(weight?.value ?? 0),
        isEstimate: weight?.isEstimate ?? false
      },
      destination: buildDestination(destination),
      transporters: transporterIds,
      ficheInterventions: ficheInterventionIds,
      packagings: buildPackagings(type as unknown as BsffType, packagings),
      forwarding:
        type === BsffType.Reexpedition && forwarding?.id ? [forwarding.id] : [],
      repackaging:
        type === BsffType.Reconditionnement
          ? (repackaging ?? []).map(r => r.id)
          : [],
      grouping:
        type === BsffType.Groupement ? (grouping ?? []).map(g => g.id) : []
    };
  }

  function buildDestination(
    destination: any
  ): BsffDestinationInput | undefined {
    if (!destination) return undefined;

    return {
      cap: destination.cap ?? undefined,
      company: cleanCompany(destination.company),
      customInfo: destination.customInfo ?? undefined,
      plannedOperationCode: destination.plannedOperationCode ?? undefined,
      reception: destination.reception?.date
        ? { date: destination.reception.date.toISOString() }
        : undefined
    };
  }

  function buildPackagings(
    type: BsffType,
    packagings: any[] | null | undefined
  ) {
    return packagings?.map(p => ({
      type: p.type,
      numero: p.numero,
      other: p.other ?? null,
      volume: p.volume ?? null,
      weight: Number(p.weight ?? 0)
    }));
  }
  async function handleUpdateFlow(input: BsffInput, draft: boolean) {
    await updateBsff({ variables: { id: bsffState.id!, input } });

    if (draft) return;

    try {
      const result = await publishBsff({ variables: { id: bsffState.id! } });
      const currentCompany = methods.getValues("emitter.company");
      if (currentCompany && input.emitter?.company) {
        methods.setValue("emitter.company", {
          ...currentCompany,
          contact: input.emitter.company.contact ?? currentCompany.contact,
          phone: input.emitter.company.phone ?? currentCompany.phone,
          mail: input.emitter.company.mail ?? currentCompany.mail
        });
      }
      return result;
    } catch (err: any) {
      setPublishErrors(handleGraphQlError(err));
      throw err;
    }
  }

  async function handleCreateFlow(input: BsffInput, draft: boolean) {
    if (draft) {
      return createDraftBsff({ variables: { input } });
    }

    try {
      const result = await createBsff({ variables: { input } });

      return result;
    } catch (err: any) {
      setPublishErrors(handleGraphQlError(err));
      throw err;
    }
  }
  useEffect(() => {
    if (createBsffError) {
      setPublishErrors(handleGraphQlError(createBsffError));
    }
  }, [createBsffError]);

  useEffect(() => {
    if (ficheError) {
      setPublishErrors(handleGraphQlError(ficheError));
    }
  }, [ficheError]);
  async function saveBsffTransporter(t: any): Promise<string> {
    const { id, transport, ...input } = t;

    const isSigned = !!t.transport?.signature?.date;

    const isExempted =
      input.recepisse?.isExempted ||
      isForeignVat(input?.company?.vatNumber) ||
      transport?.mode !== TransportMode.Road;

    const cleanInput: BsffTransporterInput = {
      ...input,
      transport: {
        mode: transport?.mode,
        plates: transport?.plates
      },
      recepisse: {
        ...input.recepisse,
        validityLimit: !!input.recepisse?.validityLimit
          ? new Date(input.recepisse.validityLimit).toISOString()
          : null,
        ...(isExempted
          ? {
              number: null,
              validityLimit: null,
              department: null
            }
          : {})
      }
    };

    if (id) {
      if (!isSigned) {
        await updateBsffTransporter({
          variables: { id, input: cleanInput }
        });
      }
      return id;
    }

    const { data } = await createBsffTransporter({
      variables: { input: cleanInput }
    });

    return data?.createBsffTransporter?.id ?? "";
  }

  return (
    <BsffContext.Provider value={bsffContext}>
      <FormStepsContent
        bsdType={BsdType.Bsff}
        draftCtaLabel={draftCtaLabel}
        isLoading={loading}
        mainCtaLabel={mainCtaLabel}
        saveForm={saveBsff}
        useformMethods={methods}
        tabsContent={tabsContent}
        sealedFields={sealedFields}
        setPublishErrors={setPublishErrors}
        errorTabIds={errorTabIds}
        genericErrorMessage={publishErrorMessages.filter(
          error => error.tabId === TabId.none
        )}
      />
      {(createBsffError || ficheError) && (
        <div className="fr-mb-8w">
          {createBsffError && (
            <DsfrNotificationError apolloError={createBsffError} />
          )}
          {ficheError && <DsfrNotificationError apolloError={ficheError} />}
        </div>
      )}
      {loading && <Loader />}
    </BsffContext.Provider>
  );
};

export default BsffFormSteps;
